import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Lock, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import Reveal from "@/components/Reveal";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function PayPage() {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ["pay", orderId],
    queryFn: async () => (await api.get(`/pay/${orderId}`)).data,
    retry: false,
  });

  const pay = async () => {
    const loaded = await loadRazorpay();
    if (!loaded) {
      toast.error("Couldn't load payments. Check your connection.");
      return;
    }
    setLoading(true);
    try {
      const pay = await api.post("/payments/create", { order_id: orderId });
      const rzp = new window.Razorpay({
        key: pay.data.key_id,
        amount: pay.data.amount,
        currency: "INR",
        name: "Abhivesha Foods",
        description: `Order ${orderId}`,
        order_id: pay.data.razorpay_order_id,
        prefill: pay.data.prefill,
        theme: { color: "#0D3220" },
        handler: async (r) => {
          try {
            await api.post("/payments/verify", { order_id: orderId, razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature });
            setPaid(true);
            setLoading(false);
          } catch {
            toast.error("Payment succeeded but verification failed. We'll confirm manually.");
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment window closed — retry whenever you're ready.");
            setLoading(false);
          },
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center pt-16"><div className="h-10 w-10 animate-spin rounded-full border-4 border-sand border-t-leaf" /></div>;
  }

  if (isError || !order) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 pt-16 text-center">
        <AlertTriangle className="h-10 w-10 text-amber-500" />
        <p className="font-display text-3xl font-extrabold text-forest">Order not found.</p>
        <Link to="/" className="rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream hover:bg-leaf">Back home</Link>
      </div>
    );
  }

  if (paid || order.status === "paid") {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-4 pb-24 pt-36 text-center" data-testid="pay-success">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-leaf/15">
          <CheckCircle2 className="h-10 w-10 text-leaf" />
        </div>
        <h1 className="font-display text-3xl font-extrabold text-forest">PAYMENT DONE.</h1>
        <p className="text-sm text-ink/60">Order {order.order_id} is paid and confirmed. A confirmation email is on its way.</p>
        <Link to={`/order/${order.order_id}`} data-testid="pay-success-link" className="mt-2 rounded-full bg-forest px-8 py-4 text-sm font-extrabold uppercase tracking-wide text-cream hover:bg-leaf">View order</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 pb-24 pt-32" data-testid="pay-page">
      <Reveal>
        <div className="text-center">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">One tap left</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-forest">COMPLETE PAYMENT.</h1>
          <p className="mt-2 text-sm text-ink/60">Order {order.order_id} is saved and waiting.</p>
        </div>

        <div className="mt-8 rounded-3xl border border-sand bg-white p-6 sm:p-8">
          <ul className="space-y-3">
            {order.items.map((i) => (
              <li key={i.name} className="flex items-center gap-3">
                <img src={i.image} alt={i.name} className="h-12 w-12 rounded-xl object-cover" />
                <p className="flex-1 text-sm font-bold text-forest">{i.name} <span className="text-ink/50">× {i.qty}</span></p>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex items-center justify-between border-t border-sand pt-4 font-display text-2xl font-extrabold text-forest">
            <span>Total</span>
            <span data-testid="pay-total">{inr(order.total)}</span>
          </div>
          <button
            data-testid="pay-now-button"
            onClick={pay}
            disabled={loading}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-leaf py-4 text-sm font-extrabold uppercase tracking-wide text-white transition-all hover:bg-forest active:scale-[0.98] disabled:opacity-60"
          >
            {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : <Lock className="h-4 w-4" />}
            {loading ? "Opening..." : `Pay ${inr(order.total)} securely`}
          </button>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] font-semibold text-ink/40">
            <RotateCcw className="h-3 w-3" /> Failed before? No money was captured — just try again.
          </p>
        </div>
      </Reveal>
    </div>
  );
}
